import React from 'react';
import { useIntl } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';

import { H3, LayoutSingleColumn, NamedLink, Page } from '../../components';
import SectionFooter from '../PageBuilder/SectionBuilder/SectionFooter';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import css from './TripCancellationSupportPage.module.css';
import MemberPlanTable from './MemberPlanTable';

const TripCancellationSupportPage = props => {
    const config = useConfiguration();
    const intl = useIntl();

    const marketplaceName = config.marketplaceName;
    const schemaTitle = intl.formatMessage({ id: 'TripCancellationSupportPage.schemaTitle' }, { marketplaceName });
    const schemaDescription = intl.formatMessage({ id: 'TripCancellationSupportPage.schemaDescription' }, { marketplaceName });
    return (
        <Page
            title={schemaTitle}
            description={schemaDescription}
        >
            <LayoutSingleColumn
                topbar={<TopbarContainer />}
                footer={<SectionFooter />}
                mainColumnClassName={css.mainColumnClassName}
            >
                <div className={css.root}>
                    <H3>
                        HouseSit Match - Trip Cancellation Support
                    </H3>
                    <p className={css.info}>
                        Working in housesitting and petsitting as we do, Housesit Match straddles the travel and home and pet care industries. Therefore, we recognise that with the best will in the world occasionally plans change due to all manner of unplanned reasons e.g. human or pet illness, cancelled travel plans etc.
                    </p>
                    <p className={css.info}>
                        In our experience, within the Sharing Economy and P-2-P arrangements, a general good will and collaborative spirit for mutual benefit minimises the number of cancellations people experience. However, we recognise that occasionally there are circumstances beyond your control and an unexpected cancellation does occur, then you may need some support.
                    </p>

                    <p className={css.subtitle}>
                        Our Trip Cancellation Support Offering
                    </p>
                    <p className={css.info}>
                        Therefore, as part of our Choice and Premium Membership plans, we now offer some Trip Cancellation Support Assurance, and a potential reimbursement should your circumstances meet the terms as listed below.
                    </p>
                    <p className={css.info}>
                        This financial reimbursement offered will ensure that as an active member of HouseSit Match you get some financial support to help you recover from your situation, should the plans for your housesit or the housesitter you have been planning for changes for genuinely unexpected reasons.
                    </p>
                    <p className={css.info}>
                        Please read on to better understand our Trip Cancellation Support offering:
                    </p>

                    <p className={css.subtitle}>
                        1. Trip Cancellation Reimbursement Service
                    </p>
                    <strong>
                        1.1 Definitions
                    </strong>
                    <p className={css.info}>
                        In the context of the "trip cancellation" service (hereinafter the “Service”), the following definitions apply:
                    </p>
                    <ul className={css.list}>
                        <li>
                            <strong>Subscription: </strong>
                            The overall offer, including a number of plans, provided by Housesit Match and subscribed to by the member. Only the Choice and Premium plans include the Service.
                        </li>
                        <li>
                            <strong>Member: </strong>
                            Any person who is not representing a business or organization and who has purchased a Subscription on the HouseSit Match website.
                        </li>
                        <li>
                            <strong>Natural Disaster: </strong>
                            An unusually intense natural event not caused by human intervention. A phenomenon, such as an earthquake, a volcanic eruption, a tidal wave, a flood, or a natural cataclysm, having been caused by an unusually intense natural event and recognized as such by public authorities.
                        </li>
                        <li>
                            <strong>Housesitting: </strong>
                            An exchange of free services between Housesitters to watch the Owner's apartment or house and take care of their pets while they are away from home.
                        </li>
                        <li>
                            <strong>Housesitter: </strong>
                            Member seeking accommodation offering a Housesitting service
                        </li>
                        <li>
                            <strong>Petsitting: </strong>
                            Petsitting: In-home pet care service.
                        </li>
                        <li>
                            <strong>Owner: </strong>
                            The Member who is offering their accommodation and looking for a Housesitting or Petsitting service.
                        </li>
                        <li>
                            <strong>Service: </strong>
                            Reimbursement of cancellation and accommodation expenses by HouseSit Match to a Member is possible if the Housesit is cancelled by either the Owner or Sitter within 10 days of the start date of the housesit.
                        </li>
                        <li>
                            <strong>Trip: </strong>
                            A stay of at least one night booked through the Nomador website.
                        </li>
                    </ul>

                    <strong>1.2 Purpose of the Service</strong>
                    <p className={css.info}>
                        <strong>For the Housesitter</strong>, the Service is intended to do the following if the Owner's trip is cancelled:
                    </p>
                    <ul className={css.list}>
                        <li>
                            Reimburse expenses associated with cancelling or changing transportation tickets; or
                        </li>
                        <li>
                            Cover accommodation expenses if the Housesitter still wishes to make the Trip
                        </li>
                    </ul>

                    <p className={css.info}>
                        <strong>For the Owner</strong>, the Service is intended to do the following if the Housesitter cancels:
                    </p>
                    <ul className={css.list}>
                        <li>
                            Cover expenses associated with caring for the pet; or
                        </li>
                        <li>
                            Cover expenses associated with cancelling or changing transportation tickets
                        </li>
                    </ul>

                    <strong>1.3 Service Performance</strong>
                    <p className={css.info}>
                        The Service begins on the day when the Choice or Premium Subscription plan was purchased through the Housesit Match website and ends on the last day of the Housesitting. The Member's Subscription must be active on the day of the Trip.
                    </p>

                    <br />
                    <strong>1.4 Limitations of the Service</strong>
                    <p className={css.info}>
                        Reimbursements paid by HouseSit Match to the Member for the Service are set based on your chosen plan and limited to a reimbursement type, as shown in the table below:
                    </p>
                    <br />
                    <MemberPlanTable />
                    <br />
                    <p className={css.info}>
                        The Member may subscribe to the higher plan at least one (1) month before the start of the Trip and no more than ten (30) days after booking the Trip.
                    </p>

                    <p className={css.subtitle}>
                        2. Service Exclusions
                    </p>
                    <p className={css.info}>
                        The following are excluded and cannot be reimbursed through the Service:
                    </p>
                    <ul className={css.list}>
                        <li>
                            Any cancellation fees arising from a Natural Disaster within 100 km of the Trip's location
                        </li>
                        <li>
                            Any cancellation fees arising from border closures or lockdowns ordered by the government or administrative authority the prevent the Trip
                        </li>
                        <li>
                            Any cancellation fees occurring more than ten (10) days before the starting date of the Housesitting
                        </li>
                        <li>
                            Any transportation expenses incurred after the Member has learned of the cancellation
                        </li>
                    </ul>
                    <p className={css.info}>
                        The Service always excludes any Member appearing on any official, governmental, or police database of proven or suspected terrorists, as well as anyone belonging to a terrorist organization, trafficking drugs, or involved as a supplier in the illegal trade of nuclear, chemical, or biological weapons.
                    </p>

                    <p className={css.subtitle}>
                        3. Practicalities of Service Performance
                    </p>
                    <strong>3.1 How is notification given for a Service cancellation?</strong>
                    <p className={css.info}>
                        The Member is required to provide notification within two (2) business days of becoming aware of the cancellation, except in the event of an accident or force majeure. The notification shall be given directly by email to <a href='mailto:lamia@housesitmatch.com'>lamia@housesitmatch.com</a> with the subject heading - ‘Trip Cancellation Reimbursement Request’. The claim will be reviewed within 10 working days.
                    </p>
                    <p className={css.info}>
                        If the Member fails to comply with this cancellation notice period and the stated terms, they will not have access to the Service.
                    </p>

                    <br />
                    <strong>3.2 What supporting documents should be provided?</strong>
                    <p className={css.info}>
                        The Member must provide the following supporting documents:
                    </p>

                    <ul className={css.list}>
                        <li>
                            Proof of any cancelled reservations;
                        </li>
                        <li>
                            Proof of expenses (paid bill for accommodations, alternative care, or transportation cancellation or change fees);
                        </li>
                        <li>
                            A copy of your confirmed Housesitting agreement confirmed through the website.
                        </li>
                    </ul>

                    <p className={css.info}>
                        Furthermore, the Member must submit any documentation that HouseSit Match deems necessary to evaluate the merits of their request.
                    </p>
                    <p className={css.info}>
                        If, proven to be acting in bad faith, or the Member's supporting documents are inaccurate or if the Member uses fraudulent means or makes an inaccurate or incomplete statement, the Member will be denied access to the Service.
                    </p>
                    <p className={css.info}>
                        In any such an event, HouseSit Match reserves the right to pursue legal action in a criminal court.
                    </p>

                    <p className={css.subtitle}>
                        4. Service Reimbursement Terms
                    </p>
                    <p className={css.info}>
                        The reimbursement will be made in full by means of a transfer into the Member's bank account within five (10) business days following the date on which HouseSit Match is in possession of all the supporting documentation, up to the maximum indicated in article 1.4.
                    </p>
                </div>
            </LayoutSingleColumn>
        </Page >
    );
};

export default TripCancellationSupportPage;